import { fetchApi } from './client'
import type { ExtractorVersion } from './types'

export async function getExtractorVersions(phenotypeId: string): Promise<ExtractorVersion[]> {
  const data = await fetchApi<{ versions: ExtractorVersion[] }>(
    `/api/v1/extractors/${encodeURIComponent(phenotypeId)}/versions`
  )
  return data.versions
}

export interface DeployedExtractor {
  extractor: {
    id: string
    version: number
    testPassRate: number | null
    deployedAt: string | null
  } | null
  extractorCode: string
  source: 'phenotype_extractors' | 'code_gen_jobs'
}

export interface PhenotypeUpload {
  id: string
  uploadId: string
  isTrainingExample: boolean
  createdAt: string
}

export interface ExtractorRunResult {
  success: boolean
  extractorCode: string
  input: {
    rates: Record<string, unknown>[]
    surcharges: Record<string, unknown>[]
    metadata: Record<string, unknown>
  }
  uploadId: string
  executionTimeMs: number
  railsExtractedData: {
    ratesCount: number
    surchargesCount: number
    rates: Record<string, unknown>[]
    surcharges: Record<string, unknown>[]
  }
}

export async function getDeployedExtractor(phenotypeId: string): Promise<DeployedExtractor> {
  return fetchApi<DeployedExtractor>(`/api/v1/extractors/${encodeURIComponent(phenotypeId)}/deployed`)
}

export async function getPhenotypeUploads(phenotypeId: string): Promise<PhenotypeUpload[]> {
  const response = await fetchApi<{ uploads: PhenotypeUpload[] }>(
    `/api/v1/extractors/${encodeURIComponent(phenotypeId)}/uploads`
  )
  return response.uploads
}

export async function runExtractor(phenotypeId: string, uploadId: string): Promise<ExtractorRunResult> {
  return fetchApi<ExtractorRunResult>(`/api/v1/extractors/${encodeURIComponent(phenotypeId)}/run`, {
    method: 'POST',
    body: { uploadId },
    timeout: 60000,
  })
}

export interface UploadExtractorInput {
  extractionFunction: string
  detectionFunction?: string
  testSuite?: string
  testPassRate?: number
  deploy?: boolean
}

export interface UploadExtractorResult {
  extractorId: string
  version: number | null
  deployed: boolean
}

/** URL for the downloadable handoff bundle (zip). Auth rides on the session cookie. */
export function contextDumpUrl(phenotypeId: string): string {
  return `/api/v1/extractors/${encodeURIComponent(phenotypeId)}/context-dump`
}

export async function uploadExtractor(
  phenotypeId: string,
  input: UploadExtractorInput
): Promise<UploadExtractorResult> {
  return fetchApi<UploadExtractorResult>(`/api/v1/extractors/${encodeURIComponent(phenotypeId)}/upload`, {
    method: 'POST',
    body: input,
    timeout: 60000,
  })
}
